import axios, { AxiosInstance } from "axios";
import io from "socket.io-client";
import { getInProgressIds, trackInProgress, untrackInProgress } from "@/helpers/inProgressTracker";
import { logger } from "./tslog";
import { getToken } from "@/utils/localstorage";
import { openContentReadyTaskpane } from "@/commands/commands";
import { useSharePricePerformanceStore } from "@/state/sharePricePerformanceStore";
import { fetchDocumentById } from "@/utils/contentmanager/documents";
import { updateDocumentPlaceholder } from "@/utils/contentmanager/documents";

// Core types for chart extraction functionality
export interface PlaceholderInfo {
  name: string;
  height: number;
  width: number;
  left: number;
  top: number;
  slideId: string;
  shapeId: string;
}

interface ChartExtractionData {
  documentId: string;
}

interface ActiveExtraction {
  resolve: (value: string) => void;
  reject: (reason?: any) => void;
  timeoutId: ReturnType<typeof setTimeout>;
  placeholder: PlaceholderInfo;
}

interface SocketConfig {
  reconnection: boolean;
  reconnectionAttempts: number;
  reconnectionDelay: number;
  timeout: number;
  forceNew: boolean;
}

interface ApiRoutes {
  agent: {
    extract: string;
  };
}

class ChartExtractionConfig {
  private readonly apiUrl: string;
  private readonly maxExtractionTimeout: number;
  private readonly routes: ApiRoutes;

  constructor() {
    this.apiUrl = process.env.API_URL || "";
    this.maxExtractionTimeout = 900000;

    this.routes = {
      agent: {
        extract: "/agent/extract-chart",
      },
    };

    this.validateConfig();
    logger.info("ChartExtractionConfig initialized");
  }

  private validateConfig(): void {
    if (!this.apiUrl) {
      logger.error("API_URL environment variable is not set");
      throw new Error("Required environment variable API_URL must be set");
    }
  }

  async getApiConfig() {
    // const token = await OfficeRuntime.auth.getAccessToken({
    //   allowSignInPrompt: false,
    //   allowConsentPrompt: false,
    // });
    const token = await getToken();

    return {
      baseURL: this.apiUrl,
      timeout: 10000,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  getSocketConfig(): SocketConfig {
    return {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 10000,
      forceNew: true,
    };
  }

  getMaxExtractionTimeout(): number {
    return this.maxExtractionTimeout;
  }

  getExtractRoute(): string {
    return this.routes.agent.extract;
  }
}

class ChartExtractionSocket {
  private socket: ReturnType<typeof io> | null = null;
  private activeExtractions: Map<string, ActiveExtraction> = new Map();
  private config: ChartExtractionConfig;
  private isConnecting: boolean = false;

  constructor(config: ChartExtractionConfig) {
    this.config = config;
    logger.info("ChartExtractionSocket initialized");
  }

  private async initializeSocket(): Promise<void> {
    if (this.socket?.connected || this.isConnecting) return;

    this.isConnecting = true;
    try {
      const apiConfig = await this.config.getApiConfig();
      this.socket = io(apiConfig.baseURL, this.config.getSocketConfig());
      await this.setupSocketEventHandlers();
      logger.info("Socket initialized successfully");
    } finally {
      this.isConnecting = false;
    }
  }


  private setupSocketEventHandlers(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.socket) return resolve();

      this.socket.on("connect", () => {
        logger.info("Socket connected");
        try {
          // Rejoin progress for any in-progress documentIds stored locally
          const ids = getInProgressIds();
          if (Array.isArray(ids) && ids.length > 0) {
            ids.forEach((id) => {
              if (id) this.socket!.emit("joinExtractRoom", id);
            });
          }
        } catch (e) {
          logger.warn("Failed to rejoin progress rooms on connect", { error: e instanceof Error ? e.message : String(e) });
        }
        resolve();
      });

      this.socket.on("disconnect", () => {
        logger.warn("Socket disconnected");
        if (this.socket?.io?.reconnectionAttempts() >= this.config.getSocketConfig().reconnectionAttempts) {
          logger.error("Socket connection failed after maximum reconnection attempts");
          this.rejectAllPendingExtractions(new Error("Socket connection failed after maximum reconnection attempts"));
        }
      });

      this.socket.on("error", (error: Error) => {
        logger.error("Socket error", { error: error.message });
        throw new Error(`Socket error: ${error.message}`);
      });

      this.socket.on("chartImageReady", this.handleChartImageReady.bind(this));
      this.socket.on("chartImageError", this.handleChartImageError.bind(this));
      // Receive incremental progress updates from Server (AgentController)
      this.socket.on("chartProgress", ({ documentId, progress, step }: { documentId: string; progress: number; step: string }) => {
        try {
          (window as any).postMessage({
            type: "CHART_PROGRESS_UPDATE",
            documentId,
            progress,
            step,
          }, "*");
        } catch (e) {
          logger.warn("Failed to dispatch chart progress update", { error: e instanceof Error ? e.message : String(e) });
        }
      });

      this.socket.on("connect_error", (error: Error) => {
        logger.error("Socket connection error", { error: error.message });
        throw new Error(`Socket connection error: ${error.message}`);
      });
    });
  }

  public async ensureConnected(): Promise<void> {
    await this.initializeSocket();
  }

  public joinRoomsFor(documentIds: string[]): void {
    if (!this.socket) return;
    documentIds.forEach((id) => {
      if (id) this.socket!.emit("joinExtractRoom", id);
    });
  }

  private async handleChartImageReady(data: ChartExtractionData): Promise<void> {
    logger.info("Chart image ready", { documentId: data.documentId });
    const extraction = this.activeExtractions.get(data.documentId);
    if (!extraction) {
      logger.warn("No active extraction found for documentId; likely a taskpane refresh. Handling gracefully.", { documentId: data.documentId });
      logger.info("Completion received after refresh; no active promise to resolve", { documentId: data.documentId });
      try { untrackInProgress(data.documentId); } catch {}
      try { (window as any).postMessage({ type: "REFRESH_CHARTS" }, "*"); } catch {}
      return;
    }

    try {
      openContentReadyTaskpane();
      (window as any).postMessage({ type: "REFRESH_CHARTS" }, "*");
      extraction.resolve(data.documentId);
      logger.info("Chart image successfully processed and inserted", { documentId: data.documentId });
    } catch (error) {
      logger.error("Failed to process chart image", { documentId: data.documentId, error });
      extraction.reject(error);
    } finally {
      this.cleanupExtraction(data.documentId);
      try { untrackInProgress(data.documentId); } catch {}
    }
  }

  private async handleChartImageError(data: ChartExtractionData): Promise<void> {
    logger.error("Chart image error", { documentId: data.documentId });
    const extraction = this.activeExtractions.get(data.documentId);
    if (!extraction) {
      logger.warn("No active extraction found for documentId", { documentId: data.documentId });
      return;
    }

    extraction.reject(new Error("Failed to process chart image"));
    this.cleanupExtraction(data.documentId);
    openContentReadyTaskpane();
    (window as any).postMessage({ type: "REFRESH_CHARTS" }, "*");
    try { untrackInProgress(data.documentId); } catch {}
  }

  private rejectAllPendingExtractions(error: Error): void {
    for (const extraction of Array.from(this.activeExtractions.entries())) {
      const [documentId, value] = extraction;
      value.reject(error);
      this.cleanupExtraction(documentId);
    }
  }

  private cleanupExtraction(documentId: string): void {
    const extraction = this.activeExtractions.get(documentId);
    if (extraction) {
      clearTimeout(extraction.timeoutId);
      this.activeExtractions.delete(documentId);
    }
  }

  async waitForChartImage(documentId: string, placeholder: PlaceholderInfo): Promise<string> {
    return new Promise(async (resolve, reject) => {
      try {
        await this.initializeSocket();
      } catch (error) {
        reject(new Error(`Failed to initialize socket: ${error instanceof Error ? error.message : "Unknown error"}`));
        return;
      }

      const timeoutId = setTimeout(() => {
        this.cleanupExtraction(documentId);
        reject(new Error("Chart extraction timed out after 5 minutes"));
      }, this.config.getMaxExtractionTimeout());

      this.activeExtractions.set(documentId, { resolve, reject, timeoutId, placeholder });

      this.socket?.emit("joinExtractRoom", documentId);
    });
  }
}

const config = new ChartExtractionConfig();
const extractionSocket = new ChartExtractionSocket(config);

export async function resumeChartProgress(): Promise<void> {
  try {
    await extractionSocket.ensureConnected();
    extractionSocket.joinRoomsFor(getInProgressIds());
  } catch (error) {
    logger.warn("Failed to resume in-progress sockets", { error });
  }
}

// Utility functions for API and data handling
async function createApiClient(): Promise<AxiosInstance> {
  const config = new ChartExtractionConfig();
  return axios.create(await config.getApiConfig());
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const binary = bytes.reduce((str, byte) => str + String.fromCharCode(byte), "");
  return window.btoa(binary);
}

function extractBase64Data(dataUrl: string | ArrayBuffer | null): string {
  if (typeof dataUrl === "string") {
    const svgPrefix = "data:image/svg+xml;base64,";
    if (dataUrl.startsWith(svgPrefix)) {
      return dataUrl.slice(svgPrefix.length);
    }

    const base64Prefix = "data:image/png;base64,";
    if (dataUrl.startsWith(base64Prefix)) {
      return dataUrl.slice(base64Prefix.length);
    }

    return dataUrl;
  }
  throw new Error("Invalid data URL format");
}

// Verify that the target placeholder shape still exists on the expected slide
export async function validatePlaceholderExists(placeholder: PlaceholderInfo, placeholderGap: any): Promise<boolean> {
  try {
    return await PowerPoint.run(async (context) => {
      if (!placeholder?.slideId) {
        return false;
      }

      const slide = context.presentation.slides.getItem(placeholder.slideId.trim());
      const shapes = slide.shapes;
      shapes.load("items");
      await context.sync();

      // Load ids, names, and dimensions for robust matching
      shapes.items.forEach((s) => s.load(["id", "name", "height", "width"]));
      await context.sync();

      const expectedShapeId = (placeholder as any)?.shapeId;
      const expectedName = placeholder.name;

      const expectedHeight = placeholder.height + (placeholderGap === 200 ? 0 : placeholderGap);
      const expectedWidth = placeholder.width;

      logger.info("validatePlaceholderExists", {
        expectedHeight,
        expectedWidth,
        expectedShapeId,
        expectedName,
      });
      const exists = shapes.items.some((s) => {
        const matchesIdentity = expectedShapeId
          ? s.id === expectedShapeId && s.name === expectedName
          : s.name === expectedName;
        const tolerance = 5; // 2-point tolerance
        const widthMatches = Math.abs(Number(s.width) - Number(expectedWidth));
        const heightMatches = Math.abs(Number(s.height) - Number(expectedHeight)) <= tolerance;
        const matchesSize = widthMatches === 0 && heightMatches;
        
        logger.info("matchesShapes", {
          matchesIdentity: matchesIdentity,
          absoluteHeight: Math.abs(Number(s.height) - Number(expectedHeight)),
          expectedHeight: expectedHeight,
          height: s.height,
          heightMatches: heightMatches,
          widthMatches: widthMatches,
          matchesSize: matchesSize,
          completeMatch: matchesIdentity && matchesSize,
        });

        return matchesIdentity && matchesSize;
      });

      return exists;
    });
  } catch (error) {
    logger.error("Failed to validate placeholder existence", { error });
    return false;
  }
}

export function base64ToSvg(base64String) {
  // Decode base64 to a UTF-8 string (the raw SVG XML)
  const decoded = atob(base64String);
  // Ensure it's treated as UTF-8
  return new TextDecoder("utf-8").decode(Uint8Array.from(decoded, (c) => c.charCodeAt(0)));
}

async function insertContent(content: string, placeholder: PlaceholderInfo, documentId: string): Promise<unknown> {
  return new Promise<void>((resolve, reject) => {
    PowerPoint.run(async (context) => {
      const slides = context.presentation.slides;
      slides.load("items");
      await context.sync();

      // Select the specific slide by ID first to avoid matching placeholders on other slides
      const slide = context.presentation.slides.getItem(placeholder.slideId.trim());
      const shapes = slide.shapes;
      shapes.load("items");
      await context.sync();
      // Load ids and names for matching
      shapes.items.forEach((s) => s.load(["id", "name"]));
      await context.sync();
      logger.info("Looking up placeholder shape (strict match)", {
        slideId: placeholder.slideId,
        expectedShapeId: (placeholder as any)?.shapeId || null,
        expectedName: placeholder.name || null,
        shapesCount: shapes.items.length,
      });
      const shape = shapes.items.find((s) => s.id === (placeholder as any)?.shapeId && s.name === placeholder.name);
      if (shape) {
        logger.info("Placeholder shape matched", { matchedId: shape.id, matchedName: shape.name });
      } else {
        const sample = shapes.items.slice(0, 10).map((s) => ({ id: s.id, name: s.name }));
        logger.warn("No matching placeholder shape found on slide (strict)", {
          slideId: placeholder.slideId,
          expectedShapeId: (placeholder as any)?.shapeId || null,
          expectedName: placeholder.name || null,
          inspectedSample: sample,
        });
        reject(new Error(`Failed to find placeholder shape '${placeholder.name}' on slide ${placeholder.slideId}`));
        return;
      }

      shape.load("id");
      await context.sync();
      context.presentation.setSelectedSlides([slide.id]);
      await context.sync();
      slide.setSelectedShapes([shape.id]);

      const options: Office.SetSelectedDataOptions = {
        coercionType: Office.CoercionType.XmlSvg,
        imageLeft: placeholder.left,
        imageTop: placeholder.top,
        imageWidth: placeholder.width,
        imageHeight: placeholder.height,
      };

      const rawSvg = base64ToSvg(content);

      context.presentation.load("slides");
      await context.sync();
      const currentSlide = context.presentation.slides.getItem(placeholder.slideId.trim());
      currentSlide.load("items");
      await context.sync();
      const currentSlideshapes = currentSlide.shapes;
      currentSlideshapes.load("items/$none");
      await context.sync();
      const currentShapeMap = new Map<string, any>();
      for (const shape1 of currentSlideshapes.items) {
        shape1.load(["name", "width", "height", "left", "top", "type"]);
        await context.sync();
        const selectionData = {
          shapeId: shape1.id,
          slideId: currentSlide.id,
          slideIndex: currentSlide.id,
          name: shape1.name,
          shapeType: shape1.type,
        };
        currentShapeMap.set(shape1.id, selectionData);
      }

      Office.context.document.setSelectedDataAsync(rawSvg, options, async (result) => {
        context.presentation.load("slides");
        await context.sync();
        const updatedCurrentSlide = context.presentation.slides.getItem(placeholder.slideId.trim());
        updatedCurrentSlide.load("items");
        await context.sync();
        const updatedcurrentSlideshapes = updatedCurrentSlide.shapes;
        updatedcurrentSlideshapes.load("items/$none");
        await context.sync();
        // Identify only the inserted chart shape among newly created shapes
        const newShapes: any[] = [];
        for (const shape1 of updatedcurrentSlideshapes.items) {
          const shapeId = shape1.id;
          if (!currentShapeMap.has(shapeId)) {
            newShapes.push(shape1);
          }
        }

        // Load properties needed to choose the primary inserted shape
        for (const ns of newShapes) {
          ns.load(["name", "width", "height", "left", "top", "type"]);
        }
        await context.sync();

        // Choose the largest area shape as the chart image container
        let targetShape: any | null = null;
        let targetShapeId: string | null = null;
        let maxArea = -1;
        for (const ns of newShapes) {
          const area = (Number(ns.width) || 0) * (Number(ns.height) || 0);
          if (area > maxArea) {
            maxArea = area;
            targetShape = ns;
            targetShapeId = ns.id;
          }
        }

        if (targetShape) {
          const selectionData = {
            shapeId: targetShape.id,
            slideId: updatedCurrentSlide.id,
            slideIndex: updatedCurrentSlide.id,
            name: targetShape.name,
            shapeType: targetShape.type,
          };

          const documentData = await fetchDocumentById(documentId);
          const documentContent = documentData?.documentContent;
          const documentDataToSave = JSON.parse(documentContent);
          const updatedDocumentContent = {
            ...documentDataToSave,
            placeholder: {
              slideNumber: documentDataToSave.placeholder.slideNumber,
              ...selectionData,
            },
          };

          await updateDocumentPlaceholder(documentId, documentData, updatedDocumentContent);

          logger.info("🆕 Inserted chart shape identified:", selectionData);
          let presentationUniqueId = Office.context.document.settings.get("ALPHASCRIPT");
          const uniqueName = `${presentationUniqueId}-${targetShape.name}-${targetShape.id}`;
          localStorage.setItem(uniqueName, documentId);

          // Add AlphaScript metadata tags ONLY to the identified chart shape
          try {
            const { metaData } = useSharePricePerformanceStore.getState();
            if (metaData.alphaScriptSettings) {
              const { workbookGuid, linkId, chartName, render, generated } = metaData.alphaScriptSettings;
              const { excelFileUrl } = metaData;

              // Use uppercase keys to match selection checks elsewhere
              if (workbookGuid) {
                targetShape.tags.add("WORKBOOKGUID", workbookGuid);
              }
              if (linkId) {
                targetShape.tags.add("LINKID", linkId);
              }
              if (chartName) {
                targetShape.tags.add("CHARTNAME", chartName);
              }
              if (render) {
                targetShape.tags.add("RENDER", render);
              }
              if (generated) {
                targetShape.tags.add("GENERATED", generated);
              }
              if (excelFileUrl) {
                targetShape.tags.add("EXCELFILEURL", excelFileUrl);
              }
              if (documentId) {
                targetShape.tags.add("DOCUMENTID", documentId);
              }

              logger.info("Added AlphaScript tags to inserted chart shape:", {
                shapeId: targetShape.id,
                tags: { workbookGuid, linkId, chartName, render, generated },
              });
            }
          } catch (error) {
            logger.warn("Failed to add AlphaScript tags to inserted chart shape:", error);
          }
        }
        if (result.status === Office.AsyncResultStatus.Failed) {
          reject(new Error(`Failed to insert image for placeholder ${placeholder.name}: ${result.error.message}`));
        } else {
          shape.delete();
          await context.sync();
          resolve();
          const slideShapesData = localStorage.getItem("slideShapesData");
          const labeledSlideShapesData = JSON.parse(slideShapesData);
          labeledSlideShapesData.targetShapeId = targetShapeId;
          labeledSlideShapesData.shapes.map((shape: any) => shape.Label === "Target Shape" ? { ...shape, id: targetShapeId } : shape);
          localStorage.setItem("slideShapesData", JSON.stringify(labeledSlideShapesData)); 
        }
      });

      await context.sync();
      return;
    }).catch((error) => {
      reject(
        new Error(
          `Failed to insert content for placeholder ${placeholder.name}: ${error instanceof Error ? error.message : "Unknown error"}`
        )
      );
    });
  });
}

// Chart processing functions
export async function downloadChartImage(documentId: string): Promise<Blob> {
  const api = await createApiClient();
  const token = await getToken();

  try {
    const response = await api.get("/document-part", {
      params: {
        documentId,
        type: "chart",
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.data.files || response.data.files.length === 0) {
      throw new Error("No chart files found");
    }

    const chartFile = response.data.files[0];
    const binaryString = window.atob(chartFile.content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    return new Blob([bytes], { type: chartFile.mimeType });
  } catch (error) {
    throw new Error(`Failed to download chart image: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}

export async function insertChartImage(blob: Blob, placeholder: PlaceholderInfo, documentId: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = async () => {
      try {
        const base64Image = extractBase64Data(reader.result as string);
        await insertContent(base64Image, placeholder, documentId);
        resolve();
      } catch (error) {
        reject(error);
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read image blob"));
    };

    reader.readAsDataURL(blob);
  });
}

async function initiateExtraction(
  documentId: string,
  documentContent: string,
  userId: string,
  powerpointId: string,
  chartPartId: string,
  excelPartId: string
): Promise<void> {
  const api = await createApiClient();
  await api.post(config.getExtractRoute(), {
    documentId,
    documentContent,
    userId,
    powerpointId,
    chartPartId,
    excelPartId,
  });
}

// Main entry point for chart extraction
export async function extractAndInsertChart(
  event?: Office.AddinCommands.Event,
  onPlaceholderObtained?: () => void
): Promise<void> {
  try {
    logger.info("Starting chart extraction process");
    await Office.onReady();
    if (onPlaceholderObtained) {
      onPlaceholderObtained();
    }

    const documentDataJSON = localStorage.getItem("ModelDataToBackend");
    const documentData = documentDataJSON ? JSON.parse(documentDataJSON) : null;
    const placeholder = documentData.placeholder;
    const modelGlobalStateJSON = localStorage.getItem("modelGlobalState");
    const modelGlobalState = modelGlobalStateJSON ? JSON.parse(modelGlobalStateJSON) : null;

    if (!placeholder) {
      logger.error("No placeholder info received");
      throw new Error("No placeholder info received");
    }

    let uuid = Office.context.document.settings.get("ALPHASCRIPT");

    let response;
    const isAlphaEdit = localStorage.getItem("alphaEdit") === "true";
    if (isAlphaEdit) {
      response = await updateDocumentData(documentData, modelGlobalState, uuid);
    } else {
      response = await saveDocumentData(documentData, modelGlobalState, uuid);
    }
    const { documentId,  chartPartId,
      excelPartId, documentContent, userId,  } = response;
    if (!documentId || !chartPartId || !excelPartId || !documentContent || !userId) {
      logger.error("No documentId, chartPartId, excelPartId, documentContent, or userId received");
      throw new Error("No documentId, chartPartId, excelPartId, documentContent, or userId received");
    }

    
    
    localStorage.setItem("TestingDtaa", JSON.stringify({ documentId, chartPartId, excelPartId, documentContent, userId }));
    
    await initiateExtraction(documentId, documentContent, userId, uuid, chartPartId, excelPartId);
    openContentReadyTaskpane();
    (window as any).postMessage({ type: "REFRESH_CHARTS" }, "*");
    logger.info("Chart extraction initiated", { documentId });
    try {
      trackInProgress(documentId);
      await extractionSocket.ensureConnected();
      extractionSocket.joinRoomsFor([documentId]);
    } catch {}
    localStorage.removeItem("ModelDataToBackend");
    localStorage.removeItem("modelGlobalState");
    localStorage.removeItem("alphaEdit");
    await extractionSocket.waitForChartImage(documentId, placeholder);

    if (event) {
      event.completed();
    }
  } catch (error) {
    logger.error("Chart extraction failed", { error });
    if (event) {
      event.completed();
    }
  }
}

async function saveDocumentData(documentContent, modelGlobalState, powerpointId) {
  const apiUrl = process.env.API_URL;
  const token = await getToken();
  const response = await axios.post(
    `${apiUrl}/document`,
    {
      documentContent: JSON.stringify(documentContent),
      llmGlobalState: JSON.stringify(modelGlobalState),
      powerpointId,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
}

async function updateDocumentData(documentContent, modelGlobalState, powerpointId) {
  const apiUrl = process.env.API_URL;
  const token = await getToken();
  const documentId = localStorage.getItem("documentId");
  const response = await axios.post(
    `${apiUrl}/document-edit`,
    {
      documentContent: JSON.stringify(documentContent),
      llmGlobalState: JSON.stringify(modelGlobalState),
      powerpointId,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        id: documentId,
      },
    }
  );
  localStorage.setItem("TestingUpdatedData", JSON.stringify({chartPart: response.data.chartPart, excelPart: response.data.excelPart}));
  return response.data;
}

function rgbToHex(r, g, b) {
  const toHex = (value) => {
    const hex = value.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbStringToHex(rgbString) {
  const [r, g, b] = rgbString
    .replace(/[^\d,]/g, "")
    .split(",")
    .map(Number);
  return rgbToHex(r, g, b);
}
